import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Cpu, Infinity as InfinityIcon, Plus, X } from 'lucide-react';
import ProductCard, { Product, Category } from '../components/ProductCard';
import { formatPrice } from '../lib/currency';
import ParticleBackground from '../components/ParticleBackground';
import { useSupabaseProducts } from '../hooks/useSupabaseProducts';
import ProductImageGallery from '../components/ProductImageGallery';
import ProductEditForm from '../components/ProductEditForm';

export default function ShopPage() {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language === 'cz' ? 'cs' : i18n.language) as 'cs' | 'en' | 'de' | 'sk';

  // Filter states for each section
  const [unlimitedFilter, setUnlimitedFilter] = useState<string>('all');
  const [roboticsFilter, setRoboticsFilter] = useState<string>('all');

  // Modal states
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedProductCategories, setSelectedProductCategories] = useState<Category[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingSection, setEditingSection] = useState<string>('');

  const isAdmin = new URLSearchParams(location.search).get('admin') === 'true' ||
                  localStorage.getItem('admin_logged_in') === 'true';

  // Use Supabase hooks for each section
  const unlimited = useSupabaseProducts('unlimited');
  const robotics = useSupabaseProducts('robotics');

  // Cleanup scroll on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new Event('modalClose'));
    };
  }, []);

  // Reset scroll if all modals are closed
  useEffect(() => {
    if (!showProductDetails && !showEditModal) {
      document.body.style.overflow = 'unset';
      window.dispatchEvent(new Event('modalClose'));
    }
  }, [showProductDetails, showEditModal]);

  const handleShowDetails = (product: Product) => {
    setSelectedProduct(product);
    // Determine which categories to use based on product category
    let categories: Category[] = [];
    if (product.category === 'unlimited') {
      categories = unlimited.categories;
    } else if (product.category === 'robotics') {
      categories = robotics.categories;
    }
    setSelectedProductCategories(categories);
    setShowProductDetails(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setEditingSection(product.category);
    setShowEditModal(true);
    // Lock scroll and hide navbar
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new Event('modalOpen'));
  };

  const closeProductDetails = () => {
    setShowProductDetails(false);
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProduct(null);
    setEditingSection('');
    // Unlock scroll and show navbar
    document.body.style.overflow = 'unset';
    window.dispatchEvent(new Event('modalClose'));
  };

  const handleSaveProduct = async (updatedProduct: Product) => {
    const section = updatedProduct.category;

    try {
      if (section === 'unlimited') {
        await unlimited.editProduct(updatedProduct);
      } else if (section === 'robotics') {
        await robotics.editProduct(updatedProduct);
      }
    } catch (error) {
      console.error('Error saving product:', error);
      // Still close modal even on error
    } finally {
      closeEditModal();
    }
  };

  const handleDelete = (id: string, section: string) => {
    if (section === 'unlimited') {
      unlimited.removeProduct(id);
    } else if (section === 'robotics') {
      robotics.removeProduct(id);
    }
  };

  const handleAddProduct = (section: string) => {
    const newProduct: Product = {
      id: '',
      name: { cs: 'Nový produkt', en: 'New product', de: 'Neues Produkt', sk: 'Nový produkt' },
      description: { cs: 'Popis produktu', en: 'Product description', de: 'Produktbeschreibung', sk: 'Popis produktu' },
      price: 1000,
      currency: 'CZK',
      category: section,
      image: '/-ct_logo_light copy copy.png',
      inStock: true,
      featured: false,
      discount: 0,
      isOnSale: false,
      isSoldOut: false,
      isRecommended: false,
      order: 0
    };

    if (section === 'unlimited') {
      unlimited.addProduct(newProduct);
    } else if (section === 'robotics') {
      robotics.addProduct(newProduct);
    }
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    // Handle drag start for admin reordering
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    // Handle drop for admin reordering
  };

  // Filter functions
  const getFilteredProducts = (products: Product[], filter: string) => {
    return filter === 'all' 
      ? products 
      : products.filter(product => product.categories?.includes(filter));
  };

  const renderCategoryFilter = (categories: Category[], currentFilter: string, setFilter: (filter: string) => void, color: string) => (
    <div className="flex flex-wrap justify-center gap-4 mb-8">
      <button
        onClick={() => setFilter('all')}
        className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
          currentFilter === 'all'
            ? `bg-${color}-500 text-white`
            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
        }`}
      >
        Všechny
      </button>
      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => setFilter(category.id)}
          className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
            currentFilter === category.id
              ? 'text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          style={{
            backgroundColor: currentFilter === category.id ? category.color : undefined
          }}
        >
          {category.name}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 pb-0 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center mb-6">
              <ShoppingCart className="w-16 h-16 text-[#EE1C23] mr-4" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('shop.title')}
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              {t('common.completeOverview')}
            </p>
          </motion.div>

          {/* Cybertronic Unlimited Section */}
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-20"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <InfinityIcon className="w-12 h-12 text-green-500 mr-4" />
                <h2 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-green-600 to-green-700 font-orbitron cursor-pointer hover:scale-105 transition-transform"
                 onClick={() => {
                   navigate(isAdmin ? '/unlimited?admin=true' : '/unlimited');
                   window.scrollTo(0, 0);
                 }}
                >
                  Cybertronic Unlimited
                </h2>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleAddProduct('unlimited')}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('common.add')}</span>
                </button>
              )}
            </div>
            
            {renderCategoryFilter(unlimited.categories, unlimitedFilter, setUnlimitedFilter, 'green')}
            
            {unlimited.loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full mb-4"></div>
                <p className="text-gray-400 text-sm">Načítání produktů…</p>
              </div>
            ) : getFilteredProducts(unlimited.products, unlimitedFilter).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {getFilteredProducts(unlimited.products, unlimitedFilter).map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={index}
                    isAdmin={isAdmin}
                    onEdit={handleEdit}
                    onDelete={(id) => handleDelete(id, 'unlimited')}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    language={currentLang}
                    categories={unlimited.categories}
                    onShowDetails={handleShowDetails}
                    sectionColor="green"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400">{t('common.noProductsInSection')}</p>
              </div>
            )}
          </motion.section>

          {/* Cybertronic Robotics Section */}
          <motion.section
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-20"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <Cpu className="w-12 h-12 text-blue-500 mr-4" />
                <h2 
                  className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 font-orbitron cursor-pointer hover:scale-105 transition-transform"
                 onClick={() => {
                   navigate(isAdmin ? '/robotics?admin=true' : '/robotics');
                   window.scrollTo(0, 0);
                 }}
                >
                  Cybertronic Robotics
                </h2>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleAddProduct('robotics')}
                  className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t('common.add')}</span>
                </button>
              )}
            </div>
            
            {renderCategoryFilter(robotics.categories, roboticsFilter, setRoboticsFilter, 'blue')}
            
            {robotics.loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full mb-4"></div>
                <p className="text-gray-400 text-sm">Načítání produktů…</p>
              </div>
            ) : getFilteredProducts(robotics.products, roboticsFilter).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {getFilteredProducts(robotics.products, roboticsFilter).map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={index}
                    isAdmin={isAdmin}
                    onEdit={handleEdit}
                    onDelete={(id) => handleDelete(id, 'robotics')}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    language={currentLang}
                    categories={robotics.categories}
                    onShowDetails={handleShowDetails}
                    sectionColor="blue"
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-400">{t('common.noProductsInSection')}</p>
              </div>
            )}
          </motion.section>

          {/* Empty state if no products at all */}
          {unlimited.products.length === 0 && robotics.products.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-black/40 backdrop-blur-md border border-red-500/20 rounded-2xl p-12 max-w-md mx-auto">
                <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-400 text-lg mb-4">
                  {t('common.noProductsAvailable')}
                </p>
                <p className="text-gray-500 text-sm">
                  {t('common.productsWillBeAdded')}
                </p>
              </div>
            </div>
          )}



          {/* Error States */}
          {(unlimited.error || robotics.error) && (
            <div className="fixed bottom-4 right-4 z-50 max-w-md">
              <div className="bg-red-900/90 border border-red-500/30 rounded-lg p-4 text-white shadow-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Chyba při načítání produktů</p>
                    {unlimited.error && <p className="text-xs text-red-300 mt-1">Unlimited: {unlimited.error}</p>}
                    {robotics.error && <p className="text-xs text-red-300 mt-1">Robotics: {robotics.error}</p>}
                  </div>
                  <button
                    onClick={() => {
                      if (unlimited.error) unlimited.reload();
                      if (robotics.error) robotics.reload();
                    }}
                    className="flex-shrink-0 text-xs bg-red-600 hover:bg-red-500 px-3 py-1 rounded transition-colors"
                  >
                    Zkusit znovu
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeProductDetails}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={`bg-gray-900 border rounded-2xl p-6 w-[90vw] max-w-2xl max-h-[90vh] overflow-y-auto relative z-[60] ${
              selectedProduct.category === 'unlimited' ? 'border-green-500/30' :
              selectedProduct.category === 'robotics' ? 'border-blue-500/30' :
              selectedProduct.category === 'sex-machines' ? 'border-pink-500/30' :
              'border-red-500/30'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">{selectedProduct.name[currentLang] || selectedProduct.name.cs}</h3>
              <button
                onClick={closeProductDetails}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ProductImageGallery product={selectedProduct} />
            
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.description')}:</h4>
                <p className="text-gray-400">
                  {selectedProduct.description[currentLang] || selectedProduct.description.cs || 'Popis není k dispozici'}
                </p>
              </div>
              
              {/* Manual download */}
              {selectedProduct.manual && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.manual')}:</h4>
                  <a
                    href={selectedProduct.manual}
                    download
                    className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <span>📄</span>
                    <span>{t('shop.downloadManual')}</span>
                  </a>
                </div>
              )}
              
              {/* Video tutorial */}
              {selectedProduct.videoTutorial && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.videoTutorial')}:</h4>
                  <a
                    href={selectedProduct.videoTutorial}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <span>{t('shop.playVideo')}</span>
                  </a>
                </div>
              )}
              
              <div>
                <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.price')}:</h4>
                <div className="flex items-center space-x-3">
                  {selectedProduct.discount && selectedProduct.discount > 0 ? (
                    <>
                      <span className="text-gray-400 line-through">
                        {formatPrice(selectedProduct.price, i18n.language)}
                      </span>
                      <span className={`font-bold text-xl ${
                        selectedProduct.category === 'unlimited' ? 'text-green-500' :
                        selectedProduct.category === 'robotics' ? 'text-blue-500' :
                        selectedProduct.category === 'sex-machines' ? 'text-pink-500' :
                        'text-[#EE1C23]'
                      }`}>
                        {formatPrice(selectedProduct.price * (1 - selectedProduct.discount / 100), i18n.language)}
                      </span>
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{selectedProduct.discount}%
                      </span>
                    </>
                  ) : (
                    <span className={`font-bold text-xl ${
                      selectedProduct.category === 'unlimited' ? 'text-green-500' :
                      selectedProduct.category === 'robotics' ? 'text-blue-500' :
                      selectedProduct.category === 'sex-machines' ? 'text-pink-500' :
                      'text-[#EE1C23]'
                    }`}>
                      {formatPrice(selectedProduct.price, i18n.language)}
                    </span>
                  )}
                </div>
              </div>
              
              {selectedProduct.categories && selectedProduct.categories.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-300 mb-2">{t('shop.categories')}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.categories.map((categoryId) => {
                      const category = selectedProductCategories.find(c => c.id === categoryId);
                      return category ? (
                        <span
                          key={categoryId} 
                          className="px-3 py-1 rounded-full text-sm font-bold text-white"
                          style={{backgroundColor: category.color}}
                        >
                          {category.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              <div className="flex space-x-4 pt-4">
                <button
                  onClick={async () => {
                    if (!selectedProduct.isSoldOut) {
                      // Close modal first
                      closeProductDetails();

                      // Add to cart with animation
                      const cart = JSON.parse(localStorage.getItem('cart') || '{}');
                      const currentQuantity = cart[selectedProduct.id] || 0;
                      cart[selectedProduct.id] = currentQuantity + 1;
                      localStorage.setItem('cart', JSON.stringify(cart));

                      // Store category
                      const cartCategories = JSON.parse(localStorage.getItem('cart_categories') || '{}');
                      cartCategories[selectedProduct.id] = selectedProduct.category;
                      localStorage.setItem('cart_categories', JSON.stringify(cartCategories));

                      // Dispatch cart update events
                      window.dispatchEvent(new Event('cartUpdated'));
                      window.dispatchEvent(new Event('globalCartUpdate'));

                      // Create floating animation
                      const centerX = window.innerWidth / 2;
                      const centerY = window.innerHeight / 2;

                      const floatingElement = document.createElement('div');
                      floatingElement.innerHTML = `
                        <div style="
                          width: 60px;
                          height: 60px;
                          background-image: url('${selectedProduct.image}');
                          background-size: cover;
                          background-position: center;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                          border: 3px solid #22c55e;
                        "></div>
                      `;

                      floatingElement.style.cssText = `
                        position: fixed;
                        left: ${centerX - 30}px;
                        top: ${centerY - 30}px;
                        z-index: 9999;
                        pointer-events: none;
                        transition: all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                        transform: scale(1);
                        opacity: 1;
                      `;

                      document.body.appendChild(floatingElement);

                      setTimeout(() => {
                        const cartIcon = document.querySelector('nav a[href="/cart"] svg');
                        if (cartIcon) {
                          const cartRect = cartIcon.getBoundingClientRect();
                          floatingElement.style.left = (cartRect.left + cartRect.width / 2 - 15) + 'px';
                          floatingElement.style.top = (cartRect.top + cartRect.height / 2 - 15) + 'px';
                          floatingElement.style.transform = 'scale(0.3)';
                          floatingElement.style.opacity = '0';
                        }
                      }, 100);

                      setTimeout(() => {
                        if (document.body.contains(floatingElement)) {
                          document.body.removeChild(floatingElement);
                        }
                      }, 1300);

                      // Create success notification
                      const currentLang = i18n.language === 'cz' ? 'cs' : i18n.language;
                      const productName = selectedProduct.name[currentLang] || selectedProduct.name.cs;

                      const notification = document.createElement('div');
                      notification.innerHTML = `
                        <div style="
                          background: linear-gradient(135deg, #22c55e, #16a34a);
                          color: white;
                          padding: 12px 20px;
                          border-radius: 12px;
                          box-shadow: 0 8px 32px rgba(34, 197, 94, 0.3);
                          font-weight: 600;
                          font-size: 14px;
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        ">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          ${t('shop.addedToCart')}: 1x ${productName}
                        </div>
                      `;

                      notification.style.cssText = `
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        z-index: 9999;
                        pointer-events: none;
                        transform: translateX(100%);
                        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                      `;

                      document.body.appendChild(notification);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(0)';
                      }, 200);

                      setTimeout(() => {
                        notification.style.transform = 'translateX(100%)';
                      }, 2500);

                      setTimeout(() => {
                        if (document.body.contains(notification)) {
                          document.body.removeChild(notification);
                        }
                      }, 3000);
                    } else {
                      closeProductDetails();
                    }
                  }}
                  className={`flex-1 text-white py-3 rounded-lg transition-all font-semibold ${
                    selectedProduct.category === 'unlimited' ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500' :
                    selectedProduct.category === 'robotics' ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500' :
                    selectedProduct.category === 'sex-machines' ? 'bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500' :
                    'bg-gradient-to-r from-[#EE1C23] to-red-600 hover:from-red-600 hover:to-[#EE1C23]'
                  }`}
                  disabled={selectedProduct.isSoldOut}
                >
                  {selectedProduct.isSoldOut ? t('shop.soldOut') : t('shop.addToCart')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && editingProduct && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-red-500/30 rounded-2xl p-8 w-[90vw] max-w-4xl max-h-[90vh] overflow-y-auto relative z-[60]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-white">Upravit produkt</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <ProductEditForm
              product={editingProduct}
              categories={
                editingSection === 'unlimited' ? unlimited.categories :
                editingSection === 'robotics' ? robotics.categories :
                []
              }
              themeColor={
                editingSection === 'unlimited' ? 'green' :
                editingSection === 'robotics' ? 'blue' :
                editingSection === 'sex-machines' ? 'pink' :
                'red'
              }
              onSave={handleSaveProduct}
              onCancel={closeEditModal}
            />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
